import { StepKey } from '../enums/stepKey';
import { FormKey } from '../enums/formKey';
import { CatalogueCategory } from '../models/services/api/getCatalogue';
import { ApplianceModel } from '../models/services/api/queryModelNumber';
import { QuoteResponse, RegOnlyResponse } from '../models/services/api/postQuote';
import { CpgData } from '../models/services/api/getCpgFinder';
import { RegistriaConciergeResponse } from '../models/services/api/postRegistria';
import { InitOptions } from '@domgen/checkout-sdk-types';

// ---------------------------------------------------------------------------
// App initialisation
// ---------------------------------------------------------------------------

export enum AppStatus {
  IDLE = 'idle',
  LOADING = 'loading',
  READY = 'ready',
  ERROR = 'error',
}

export type AppState = {
  status: AppStatus;
  session: { id: string; dateCreated: string } | null;
  catalogue: CatalogueCategory[] | null;
};

export type UrlParams = {
  mn?: string;
  sn?: string;
  rs?: string;
  matchedAppliance?: ApplianceModel;
};

// ---------------------------------------------------------------------------
// Form data
// ---------------------------------------------------------------------------

export type ApplianceFormData = {
  [StepKey.APPLIANCE_DETAILS]?: {
    [FormKey.SUPER_CATEGORY]?: string;
    [FormKey.ITEM_CATEGORY]?: string;
  };
  [StepKey.MODEL_SERIAL]?: {
    [FormKey.MODEL_NUMBER]?: string;
    [FormKey.SERIAL_NUMBER]?: string;
  };
  [StepKey.PURCHASE_PRICE]?: {
    [FormKey.PURCHASE_PRICE]?: string;
  };
  [StepKey.ARRIVAL_DATE]?: {
    [FormKey.ARRIVAL_DATE]?: string;
  };
  [StepKey.PERSONAL_DETAILS]?: {
    [FormKey.FIRST_NAME]?: string;
    [FormKey.LAST_NAME]?: string;
    [FormKey.EMAIL]?: string;
    [FormKey.PHONE_DIALLING_CODE]?: string;
    [FormKey.PHONE_NUMBER]?: string;
    [FormKey.ADDRESS_ID]?: string;
    [FormKey.ADDRESS_LINE_1]?: string;
    [FormKey.ADDRESS_LINE_2]?: string;
    [FormKey.ADDRESS_IS_FORMATTED]?: boolean;
    [FormKey.CITY]?: string;
    [FormKey.STATE_CODE]?: string;
    [FormKey.POSTAL_CODE]?: string;
    [FormKey.COUNTRY_CODE]?: string;
    [FormKey.MERCHANDISE_ADDRESS_ID]?: string;
    [FormKey.MERCHANDISE_ADDRESS_LINE_1]?: string;
    [FormKey.MERCHANDISE_ADDRESS_LINE_2]?: string;
    [FormKey.MERCHANDISE_CITY]?: string;
    [FormKey.MERCHANDISE_STATE_CODE]?: string;
    [FormKey.MERCHANDISE_POSTAL_CODE]?: string;
    [FormKey.MERCHANDISE_IS_FORMATTED]?: boolean;
  };
};

export type NextEventData =
  | { [StepKey.APPLIANCE_DETAILS]: ApplianceFormData[StepKey.APPLIANCE_DETAILS] }
  | { [StepKey.MODEL_SERIAL]: ApplianceFormData[StepKey.MODEL_SERIAL] }
  | { [StepKey.PURCHASE_PRICE]: ApplianceFormData[StepKey.PURCHASE_PRICE] }
  | { [StepKey.ARRIVAL_DATE]: ApplianceFormData[StepKey.ARRIVAL_DATE] }
  | { [StepKey.PERSONAL_DETAILS]: ApplianceFormData[StepKey.PERSONAL_DETAILS] };

// ---------------------------------------------------------------------------
// Quote
// ---------------------------------------------------------------------------

export enum QuoteStatus {
  LOADING = 'loading',
  ERROR = 'error',
  READY = 'ready',
}

export type QuoteState = {
  status: QuoteStatus;
  data?: QuoteResponse;
};

// ---------------------------------------------------------------------------
// Checkout
// ---------------------------------------------------------------------------

export type CheckoutState = {
  loading: boolean;
  data?: InitOptions;
};

// ---------------------------------------------------------------------------
// Confirmation
// ---------------------------------------------------------------------------

export type ConfirmationState = {
  regOnlyData?: RegOnlyResponse;
  purchaseData?: ConfirmationPurchaseData;
  offerData?: ConfirmationOfferData;
  cpgData?: CpgData;
  conciergeData?: RegistriaConciergeResponse;
  applianceDetails?: {
    applianceName?: string;
    categoryName?: string;
    modelNumber?: string;
    serialNumber?: string;
    purchasePrice?: string;
    arrivalDate?: string;
    guarantee?: number;
  };
};

export type ConfirmationOfferData = {
  discountCode?: string;
  startDate?: string;
  netFirstPaymentAmount?: number;
  netFirstPaymentAmountAfterPromotionalPeriod?: number;
  itemId?: string;
};

export type ConfirmationPurchaseData = {
  planNumber: string;
  transactionId: string;
  paymentType: 'card' | 'ach';
  mappedPaymentType: 'cc' | 'ach';
  netFirstPaymentAmountFromPurchase?: number;
  netFirstPaymentAmountAfterPromotionalPeriodFromPurchase?: number;
  discountCodeFromPurchase?: string;
  discountTermFromPurchase?: number;
};

// ---------------------------------------------------------------------------
// Shared data capture
// ---------------------------------------------------------------------------

export type GaEventData = {
  netFirstPaymentAmount?: number;
  salesWidgetClicked?: boolean;
};

// ---------------------------------------------------------------------------
// Combined store context
// ---------------------------------------------------------------------------

export type StoreContext = {
  app: AppState;
  form: ApplianceFormData;
  urlParams?: UrlParams;
  applianceLookupResults?: ApplianceModel[];
  quote?: QuoteState;
  confirmation?: ConfirmationState;
  checkout?: CheckoutState;
  gaEventData?: GaEventData;
};

export const initialContext: StoreContext = {
  app: {
    status: AppStatus.IDLE,
    session: null,
    catalogue: null,
  },
  form: {},
};

export enum StoreEvent {
  INITIALISE = 'INITIALISE',
  CREATE_SESSION = 'CREATE_SESSION',
  SET_SESSION = 'SET_SESSION',
  SESSION_CREATION_FAILED = 'SESSION_CREATION_FAILED',
  GET_CATALOGUE = 'GET_CATALOGUE',
  SET_CATALOGUE = 'SET_CATALOGUE',
  GET_CATALOGUE_FAILED = 'GET_CATALOGUE_FAILED',
  PROCESS_URL_PARAMS = 'PROCESS_URL_PARAMS',
  SET_URL_PARAM_RESULTS = 'SET_URL_PARAM_RESULTS',
  SAVE_APPLIANCE_MODEL_RESULTS = 'SAVE_APPLIANCE_MODEL_RESULTS',
  NEXT = 'NEXT',
  BACK = 'BACK',
  SUBMIT = 'SUBMIT',
  QUOTE_FAILED = 'QUOTE_FAILED',
  SET_QUOTE = 'SET_QUOTE',
  NO_QUOTE_REG_ONLY = 'NO_QUOTE_REG_ONLY',
  REG_ONLY = 'REG_ONLY',
  SET_CONCIERGE_DATA = 'SET_CONCIERGE_DATA',
  SET_CPG_DATA = 'SET_CPG_DATA',
  CONTINUE_TO_CHECKOUT = 'CONTINUE_TO_CHECKOUT',
  SET_CHECKOUT = 'SET_CHECKOUT',
  CHECKOUT_SUCCESS = 'CHECKOUT_SUCCESS',
  CHECKOUT_FAILED = 'CHECKOUT_FAILED',
  RESTORE_SESSION = 'RESTORE_SESSION',
  START_SESSION_TIMER = 'START_SESSION_TIMER',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
}
